import { useState } from 'react';
import { css } from '@emotion/react';
import { UseNavigateResult } from '@tanstack/react-router';
import { useTranslation } from '@frontend/i18n';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { BackIcon, Button, FormRow, Heading, Text, TextField, TextLink, useForm } from '@frontend/design-system';
import { redirectToIdPLogin } from '../services/sso-redirect';
import { clearRememberedSsoTenant } from '../services/sso-storage';
import { FormWrap } from './form-wrap';
import { SignInError, SignInErrorTypeKey } from './sign-in-error.component';

interface Props<T extends string> {
  tenant?: string;
  navigate: UseNavigateResult<T>;
}

export const SsoTenantLogin = <T extends string>({ tenant, navigate }: Props<T>) => {
  const { t } = useTranslation('SignIn');
  const [errorCode, setErrorCode] = useState<SignInErrorTypeKey | undefined>(undefined);
  const [isError, setIsError] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const { formProps, getFieldProps } = useForm({
    fields: {
      tenant: { type: 'text', required: true, value: tenant ?? '' },
    },
    onSubmit: async (data) => {
      try {
        setIsPending(true);
        await redirectToIdPLogin(data.tenant ?? '');
      } catch (e: any) {
        console.error(e);
        setErrorCode('ssoTenantNotFound');
        setIsError(true);
        setIsPending(false);
        sentry.error({
          topic: 'auth',
          team: 'users-infra',
          error: e,
        });
      }
    },
  });

  return (
    <>
      <FormWrap>
        <Heading>{t('Log in with SSO')}</Heading>
        <Text>{t('Enter SSO identifier')}</Text>
        <form {...formProps} id='sso-tenant-login-form' data-testid='sso-tenant-login-form'>
          <FormRow
            css={css`
              text-align: initial;
              margin: ${theme.spacing(4, 0)};
            `}
          >
            <TextField {...getFieldProps('tenant')} label={t('SSO Identifier')} />
          </FormRow>
          <Button
            variant='primary'
            size='large'
            type='submit'
            css={{ width: '100%' }}
            id='continue-to-sso-login-button'
          >
            {t('Continue to SSO Login')}
          </Button>
        </form>
        <TextLink
          css={css`
            display: flex;
            margin: ${theme.spacing(4, 0, 0)};
            cursor: pointer;
            text-align: left;
            width: 100%;
          `}
          disabled={isPending}
          onClick={() => {
            clearRememberedSsoTenant();
            navigate({ to: '/' });
          }}
        >
          <>
            <BackIcon
              css={css`
                margin-right: ${theme.spacing(1)};
              `}
            />
            {t('Back to login')}
          </>
        </TextLink>
      </FormWrap>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin: 0 15%;
          min-width: 300px;
          max-width: 400px;
        `}
      >
        {isError ? <SignInError errorCode={errorCode} /> : null}
      </div>
    </>
  );
};
